import { ReactComponent as AddIcon } from '$icons/Add.svg';
import { ReactComponent as CloseIcon } from '$icons/CloseGreen.svg';
import { ReactComponent as RightArrow } from '$icons/ArrowRight.svg';
import Button from '$components/Buttons/Button';
import ModalInTailwind from '$components/ModalInTailwind/ModalInTailwind';
import { ReactComponent as Copy } from '$icons/copy.svg';
import { useState } from 'react';
import { useLazyGetShareCandidateQuery } from '$store/modules/candidates';
import { errorHandler, successHandler } from '$store/utils';
import copyToClipboard from '$utils/copyToClipboard';
import { useForm } from 'react-hook-form';

type ShareRosterFormType = {
  title: string;
  candidatesIds: string;
};

const ShareRoster = () => {
  const [openShareRoster, setOpenShareRoster] = useState(false);
  const [shareRosterUrl, setShareRosterUrl] = useState('');
  const { register, handleSubmit, watch, reset } = useForm<ShareRosterFormType>(
    {
      defaultValues: {
        title: '',
        candidatesIds: '',
      },
    }
  );

  const sharedRosterTitle = watch('title');
  const sharedRosterCandidatesIds = watch('candidatesIds');

  const [getShareCandidates] = useLazyGetShareCandidateQuery();

  const closeModalHandler = () => {
    setOpenShareRoster(false);
  };

  const handleGenerateShareRoster = async (payload: ShareRosterFormType) => {
    const ids = payload.candidatesIds
      .replace(/[^,|\d|\n]/g, '')
      ?.split(/,|\n/g)
      ?.filter((singleCandidateId: string) => !!singleCandidateId)
      ?.map((singleId: string) => parseInt(singleId, 10));
    setShareRosterUrl('Generating URL...');

    await getShareCandidates({
      name: payload.title,
      ids,
    }).then(async ({ data }) => {
      if (data) {
        const rosterUrl = new URL(window.location.origin);
        rosterUrl.pathname = `share/candidates/${data?.encodedId}`;
        setShareRosterUrl(rosterUrl.toString());
        await copyToClipboard(rosterUrl.toString()).then(() => {
          successHandler(
            'Share Candidate Roster URL copied to clipboard',
            true
          );
        });
        reset();
      } else {
        setShareRosterUrl('Error retrieving share roster id, try again later');
        errorHandler(
          '[GetShareCandidates]',
          'Error retrieving share roster id, try again later',
          data,
          true
        );
      }
    });
  };

  const copyToClipboardHandler = async () => {
    await copyToClipboard(shareRosterUrl).then(() => {
      successHandler('Share Candidate Roster copied to clipboard', true);
    });
  };

  return (
    <>
      <Button
        bgColor="#006789"
        showText
        text="Generate Roster"
        icon={AddIcon}
        invertTextContent
        styling="px-4 whitespace-nowrap"
        onClick={() => {
          setOpenShareRoster((prev) => !prev);
        }}
      />
      <ModalInTailwind isOpen={openShareRoster} closeModal={closeModalHandler}>
        <form
          onSubmit={handleSubmit(handleGenerateShareRoster)}
          className="flex flex-col bg-white p-8 w-2/5 rounded"
        >
          <header className="flex flex-row justify-between items-center mb-4 w-full">
            <div className="text-xl font-bold whitespace-nowrap">
              Share Candidate Roster
            </div>
            <button
              aria-label="Close"
              type="button"
              onClick={closeModalHandler}
            >
              <CloseIcon />
            </button>
          </header>
          <div className="flex flex-col w-full">
            <h3 className="text-lg font-semibold">
              Title of the Candidate Roster
            </h3>
            <input
              className="border rounded w-full p-2 mb-4"
              placeholder="Roster Title"
              {...register('title', { required: true })}
            />
            <h3 className="text-lg font-semibold">Candidate Id&apos;s</h3>
            <textarea
              data-testid="video-prompt-textarea"
              className="border rounded w-full min-h-20 p-2 mb-4"
              placeholder={`Type the candidate id's that you want to share, separated by line or comma.`}
              {...register('candidatesIds', {
                required: true,
              })}
            />
            <div className="flex flex-row">
              <input
                className="border rounded-l w-full p-2"
                placeholder="Generated URL will be displayed here"
                readOnly
                disabled
                value={shareRosterUrl}
              />
              <button
                disabled={!shareRosterUrl}
                onClick={copyToClipboardHandler}
                type="button"
                className="flex flex-row gap-2 items-center border rounded-r border-gray-200 border-l-0 border-solid text-gray-500 px-2 disabled:cursor-not-allowed"
              >
                Copy <Copy className="opacity-40" />
              </button>
            </div>
            <div className="flex flex-row mt-4 self-end w-60">
              <Button
                text="Cancel"
                showText
                textStyles={{ color: '#079e88' }}
                onClick={closeModalHandler}
              />
              <Button
                text="Generate"
                icon={RightArrow}
                invertTextContent
                showText
                bgColor="#079e88"
                disabled={
                  !sharedRosterTitle || sharedRosterCandidatesIds.length === 0
                }
                customStyles={
                  (!sharedRosterTitle ||
                    sharedRosterCandidatesIds.length === 0) && {
                    opacity: '.25',
                  }
                }
                submit
              />
            </div>
          </div>
        </form>
      </ModalInTailwind>
    </>
  );
};
export default ShareRoster;
